<template>
  <main class="list-container">
    <div
      v-for="(section, index) in sections"
      :id="section.id ?? undefined"
      :key="index"
      class="section"
    >
      <!-- Первый заголовок документа — это его название, он же h1 страницы:
           без него у юридических страниц вообще не было заголовка первого
           уровня. Вид не меняется, оформление задаёт класс. -->
      <component
        :is="index === mainTitleIndex ? 'h1' : 'h2'"
        v-if="section.title"
        class="section__title"
      >{{ section.title }}</component>
      <template
        v-for="(item, itemIndex) in section.items"
        :key="itemIndex"
      >
        <!-- Оглавление: на прежней странице это колонка ссылок, а не
             абзацы. Выводим на том же месте, где оно стоит в документе. -->
        <div
          v-if="item.kind === 'toc'"
          class="section__toc"
        >
          <nuxt-link
            v-for="(link, linkIndex) in item.links"
            :key="linkIndex"
            :to="наЯзыке(tocHref(link))"
            class="section__link"
          >{{ tocLabel(link) }}</nuxt-link>
        </div>

        <!--
          Списки рисуем списком, а не абзацем.
          Раньше сюда попадал любой блок, и его содержимое разбиралось как
          строка текста. У orderedList внутри лежат listItem, у которых
          собственного текста нет, — пункты молча исчезали со страницы. Так
          пропал, например, перечень прав пользователя в согласии на обработку
          данных: заголовок и двоеточие на месте, а списка под ними нет.
        -->
        <component
          :is="item.ordered ? 'ol' : 'ul'"
          v-else-if="item.kind === 'list'"
          :start="item.start ?? undefined"
          class="section__list"
          :class="item.ordered ? 'section__list--ordered' : 'section__list--bullet'"
        >
          <li
            v-for="(paragraphs, liIndex) in item.items"
            :key="liIndex"
            class="section__list-item"
          >
            <p
              v-for="(paragraph, pIndex) in paragraphs"
              :key="pIndex"
              class="section__paragraph"
            >
              <legal-inline :nodes="paragraph.content ?? []" />
            </p>
          </li>
        </component>

        <p
          v-else
          :id="blockId(item.block)"
          class="section__paragraph"
        >
          <legal-inline :nodes="item.block.content ?? []" />
        </p>
      </template>
    </div>

    <div
      v-if="!sections.length"
      class="section"
    >
      <p class="section__paragraph">{{ $t('legal.notPublished') }}</p>
    </div>
  </main>
</template>

<script setup lang="ts">
type TiptapMark = { type?: string, attrs?: Record<string, unknown> }

type TiptapNode = {
  type: string
  text?: string
  marks?: TiptapMark[]
  attrs?: Record<string, unknown> | null
  content?: TiptapNode[]
}

type LegalDocument = {
  title: string
  slug: string
  content?: { type?: string, content?: TiptapNode[] } | null
}

const props = defineProps<{
  /** slug документа в Strapi */
  slug: string
}>()

// Читаем опубликованное состояние — Strapi по умолчанию отдаёт именно его,
// то есть на сайте всегда последняя опубликованная редакция. Архив прежних
// редакций хранится отдельно и на выдачу не влияет.
const { data: response } = await useApi<{ data: LegalDocument[] }>('legal-documents', {
  query: computed(() => ({
    'filters[slug][$eq]': props.slug
  }))
})

const document = computed(() => response.value?.data?.[0] ?? null)

const blocks = computed<TiptapNode[]>(() => {
  const root = document.value?.content?.content ?? []
  // содержимое обёрнуто в richBlock — разворачиваем
  return root.flatMap(node => node.type === 'richBlock' ? (node.content ?? []) : [node])
})

/**
 * Разбиваем плоский список блоков на карточки-секции: каждая начинается
 * заголовком и включает идущие за ним абзацы. Так страница выглядит ровно
 * как прежняя вёрстка политики, где каждый раздел — отдельная карточка.
 */
const textOf = (node: TiptapNode): string =>
  node.text ?? (node.content ?? []).map(textOf).join('')

type SectionItem =
  | { kind: 'toc', links: TiptapNode[] }
  | { kind: 'list', ordered: boolean, start: number | null, items: TiptapNode[][] }
  | { kind: 'p', block: TiptapNode }

type Section = { title: string | null, id: string | null, items: SectionItem[] }

/**
 * Разбиваем плоский список блоков на карточки-разделы: каждая начинается
 * заголовком и включает идущие за ним абзацы. Так страница выглядит ровно
 * как прежняя вёрстка политики.
 *
 * Порядок блоков внутри раздела сохраняется: оглавление стоит там же, где
 * в документе, а не выносится вперёд. Идущие подряд пункты оглавления
 * собираются в одну колонку ссылок.
 */
const sections = computed<Section[]>(() => {
  const result: Section[] = []

  const current = (): Section => {
    if (!result.length) result.push({ title: null, id: null, items: [] })
    return result[result.length - 1]!
  }

  for (const block of blocks.value) {
    if (block.type === 'heading') {
      const id = block.attrs?.id
      result.push({
        title: textOf(block),
        id: typeof id === 'string' && id ? id : null,
        items: []
      })
      continue
    }

    const section = current()

    if (block.attrs?.toc) {
      const last = section.items[section.items.length - 1]
      if (last?.kind === 'toc') last.links.push(block)
      else section.items.push({ kind: 'toc', links: [block] })
      continue
    }

    if (block.type === 'orderedList' || block.type === 'bulletList') {
      const start = block.attrs?.start
      section.items.push({
        kind: 'list',
        ordered: block.type === 'orderedList',
        start: typeof start === 'number' && start !== 1 ? start : null,
        /* Пункт списка — это набор абзацев, а не строка: так его кладёт редактор. */
        items: (block.content ?? []).map(item => item.content ?? [])
      })
      continue
    }

    section.items.push({ kind: 'p', block })
  }

  return result.filter(s => s.title || s.items.length)
})

const { t } = useI18n()

/** Название документа стоит первым заголовком — оно и становится h1 */
const mainTitleIndex = computed(() => sections.value.findIndex(section => section.title))

/** Якорь абзаца: на него ведут ссылки оглавления */
const blockId = (block: TiptapNode): string | undefined => {
  const id = block.attrs?.id
  return typeof id === 'string' && id ? id : undefined
}

const localePath = useLocalePath()

/**
 * Адрес на текущем языке.
 *
 * В содержимом ссылки записаны без языка ('/regulations/…'), и с английской
 * версии документа такая ссылка уводила на русскую. Якоря и чужие адреса не
 * трогаем: у якоря нет языка, у внешнего сайта — наших маршрутов.
 *
 * Внутри текста тем же занимается LegalInline — здесь это нужно только
 * пунктам оглавления, они рисуются отдельно от строки текста.
 */
const наЯзыке = (href: string | null): string | null => {
  if (!href || !href.startsWith('/') || href.startsWith('//')) return href
  return localePath(href) || href
}

const linkHref = (node: TiptapNode): string | null => {
  const mark = (node.marks ?? []).find(m => m?.type === 'link')
  const href = mark?.attrs?.href
  return typeof href === 'string' && href ? href : null
}

const tocHref = (block: TiptapNode): string =>
  linkHref(block.content?.[0] ?? {} as TiptapNode) ?? '#'

const tocLabel = (block: TiptapNode): string => textOf(block).trim()


/**
 * Мета юридического документа. Заголовок приходит из Strapi, поэтому теги
 * ставятся здесь, а не на странице: страниц две (/privacy-policy и
 * /documents/<slug>), а документ и его название — одни и те же.
 *
 * Описание собирается из первых абзацев самого документа: писать его руками
 * значило бы держать вторую копию текста, которая разойдётся с редакцией из
 * админки.
 */
const documentTitle = computed(() => document.value?.title ?? t('legal.fallbackTitle'))

const documentSummary = computed(() => {
  // Берём только абзацы: пункты оглавления — это ссылки, из них складывается
  // перечень заголовков, а не пересказ документа.
  const text = sections.value
    .flatMap(section => section.items)
    .flatMap((item) => {
      if (item.kind === 'p') return [textOf(item.block)]
      /* Пункты списка — тоже текст документа, а вот оглавление это ссылки. */
      if (item.kind === 'list') return item.items.flatMap(paragraphs => paragraphs.map(textOf))
      return []
    })
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim()

  if (!text) {
    return `${documentTitle.value} ${t('legal.seoSuffix')}`
  }

  return text.length > 300 ? `${text.slice(0, 297).trimEnd()}…` : text
})

useAdfSeo({
  title: documentTitle,
  description: documentSummary,
  type: 'article',
  // Юридические документы существуют только на русском: под /en тот же текст
  onlyDefaultLocale: true,
})
</script>

<style scoped lang="scss">
/* Оформление перенесено один в один со старой страницы политики
   конфиденциальности, чтобы её вид не изменился. Тот же файл теперь
   обслуживает и остальные юридические документы — оформление у них
   единое. */
.list-container {
  background: linear-gradient(90deg, #0f0a2d 0%, #07051e 100%);

  padding-block: 160px;

  display: flex;
  flex-direction: column;
  gap: 16px;

  @media screen and (max-width: 764px) {
    padding-inline: 16px;
  }

  .section {
    background-color: #1e1137;
    box-shadow: 0px 1px 12px 0px #0a072426;

    /* Шапка сайта зафиксирована и перекрывает верх страницы, поэтому
       переход по якорю прятал заголовок раздела под ней. Отступ равен
       высоте шапки плюс небольшой зазор. */
    scroll-margin-top: 120px;

    @media screen and (max-width: 764px) {
      scroll-margin-top: 96px;
    }

    padding: 32px;
    border-radius: 16px;

    width: min(952px, 100%);
    margin-inline: auto;

    display: flex;
    flex-direction: column;
    gap: 24px;

    @media screen and (max-width: 764px) {
      padding-inline: 24px 16px;
    }

    &__title {
      color: #fff;
      font-family: 'Cera Pro', sans-serif;
      font-weight: 700;
      font-size: 32px;
      line-height: 40px;
      letter-spacing: 0%;
    }

    &__paragraph {
      color: #fff;
      font-family: 'Cera Pro', sans-serif;
      font-weight: 400;
      font-size: 16px;
      line-height: 24px;
      letter-spacing: 0%;

      & .underline,
      & a.underline {
        color: #fff;
        text-decoration: underline;
      }
    }

    /* Списки внутри документа: тот же текст, что в абзацах, с маркером
       слева. Отступ даём паддингом, а не марджином, — иначе номер уезжает
       за край карточки раздела. */
    &__list {
      margin: 0;
      padding-left: 24px;
      display: flex;
      flex-flow: column;
      gap: 8px;
    }

    &__list--ordered { list-style: decimal; }
    &__list--bullet { list-style: disc; }

    &__list-item {
      color: #fff;

      &::marker {
        color: #fff;
        font-family: 'Cera Pro', sans-serif;
        font-size: 16px;
      }
    }

    &__toc {
      display: flex;
      flex-flow: column;
    }

    &__link {
      color: #be90fe;
      text-decoration: none;
      font-family: 'Cera Pro', sans-serif;
      font-weight: 500;
      font-size: 16px;
      line-height: 24px;
      letter-spacing: 0%;
    }
  }
}
</style>
